<template>
  <ul>
    <!-- Dashboard -->
    <SidebarMenuItem
      :item-links="[{ title: 'Home', url: '/dashboard/home' }]"
      icon="HomeSmall"
    />

    <!-- Asset Org Chart -->
    <SidebarMenuItem
      :item-links="[{ title: 'Assets', url: '/dashboard/assets' }]"
      icon="SquaresPlus"
    />

    <!-- ESG Metrics (Data Configuration) -->
    <SidebarMenuItem
      v-if="$featureFlags.esgMetrics && isCompanyAdmin"
      :item-links="[
        { title: 'Data Configuration', url: '/dashboard/esg-metrics' },
      ]"
      icon="GrobalEuropeAfricaIcon"
    />

    <!-- ESG Data -->
    <SidebarMenuItem
      v-if="$featureFlags.esgDataEntryPage"
      :item-links="[
        {
          title: 'ESG Data Entry',
          url: '/dashboard/data-entry/esg-data-entry',
        },
      ]"
      icon="CloudIcon"
    />

    <!-- Data Upload Page -->
    <!-- completely hide this button if the user isnt assigned to any categories -->
    <SidebarMenuItem
      :item-links="dataUploadButtons"
      menu-title="GHG Data"
      icon="ViewLog"
    />

    <!-- Report -->
    <SidebarMenuItem
      :item-links="reportingLinks"
      :sidebar-active-status="sideBarActive"
      icon="ReportingIcon"
    />

    <!-- Decarbonization -->
    <SidebarMenuItem
      :item-links="[
        { title: 'Target Setting', url: '/dashboard/ghg-overview' },
        { title: 'Action Plan', url: '/dashboard/abatement/' },
      ]"
      menu-title="Decarbonization"
      :sidebar-active-status="sideBarActive"
      icon="AbatementIcon"
    />

    <!-- Supplier Portal -->
    <div
      v-if="supplierPortal"
      @mouseover="dropRightSupplierPortal = true"
      @mouseleave="dropRightSupplierPortal = false"
    >
      <li class="rounded-sm relative mb-0.5 last:mb-0 select-none">
        <a
          href="https://app.powerbi.com/Redirect?action=OpenApp&appId=fae875ca-799b-43c0-a0f8-a0778f87c61d&ctid=f4ea409d-d896-4e1e-9412-8dc02a49613a"
          target="_blank"
          class="relative z-10 block truncate transition duration-150 text-white/60 hover:text-white"
          title="Supplier Portal"
        >
          <div
            class="flex items-center py-3 md:py-3"
            :class="{ 'p-0 justify-center': !sideBarActive, 'md:px-3 md:py-3 lg:px-3': sideBarActive }"
          >
            <div
              class="flex items-center menu-inner"
              :class="{ 'mx-auto': !sideBarActive, 'm-0': sideBarActive }"
            >
              <component
                :is="'LayerPlus'"
                class="w-5 h-5"
                :class="{ 'mr-0': !sideBarActive, 'mr-3': sideBarActive }"
              />
              <span
                class="hidden ml-1 text-sm font-medium duration-200 lg:sidebar-expanded:opacity-100 2xl:opacity-100"
                :class="{ 'md:hidden lg:block': sideBarActive }"
              >Supplier Portal</span>
            </div>
          </div>
        </a>
        <!-- Dropright Menu -->
        <div
          v-if="dropRightSupplierPortal"
          class="absolute z-50 left-8 md:left-12 lg:left-11 z-9 top-0 min-w-[220px] bg-gray-200 flex flex-col"
          :class="{ '!hidden': sideBarActive }"
        >
          <div class="flex flex-col p-3 bg-sidebar-green-primary">
            <a
              href="https://app.powerbi.com/Redirect?action=OpenApp&appId=fae875ca-799b-43c0-a0f8-a0778f87c61d&ctid=f4ea409d-d896-4e1e-9412-8dc02a49613a"
              class="text-sm text-white/60"
            >
              <span>Supplier Portal</span>
            </a>
          </div>
        </div>
      </li>
    </div>
    <!-- Settings -->
    <SidebarMenuItem
      :item-links="[
        { title: 'My Companies', url: '/dashboard/companies' },
        {
          title: 'Users',
          url: '/dashboard/settings/users',
          hidden: !isCompanyAdmin,
        },
        {
          title: 'Permissions',
          url: '/dashboard/settings/permissions',
          hidden: !isCompanyAdmin,
        },
        {
          title: 'My Profile',
          url: '/dashboard/settings/general',
          hidden: false,
        },
        {
          title: 'Integration',
          url: '/dashboard/settings/integration',
          hidden: !isCompanyAdmin,
        },
      ]"
      menu-title="Settings"
      icon="CogIcon"
    />
  </ul>
</template>

<script>
export default {
  name: 'UserMenu',

  data() {
    return {
      categoriesAssignedToThisUser: [],
      categoriesThisUserIsAnApproverOf: [],
      supplierPortal: false,
      dropRightSupplierPortal: false,
      reportingExternalEnabled: false,
    }
  },

  computed: {
    active() {
      return this.$store.sidebar.isActive
    },
    sideBarActive() {
      return this.$store.sidebar.responsiveActive
    },
    dataUploadButtons() {
      const items = []

      if (this.categoriesAssignedToThisUser.length) {
        items.push({ title: 'GHG Data Entry', url: '/dashboard/data-upload' })
      }
      if (this.isCompanyAdmin) {
        items.push({ title: 'GHG Progress Tracker', url: '/dashboard/ghg-progress-tracker' })
      }
      if (this.categoriesThisUserIsAnApproverOf.length) {
        items.push({
          title: 'GHG Data Approval',
          url: '/dashboard/ghg-approval',
        })
      }

      return [
        ...items,
        {
          title: 'Quantitative Summary',
          url: '/dashboard/reporting/quantitative-report',
        },
        { title: 'Emission Audit Log', url: '/dashboard/emission-audit-log' },
      ]
    },
    reportingLinks() {
      if (this.reportingExternalEnabled) {
        return [{ title: 'Report', url: '/reporting/reports', external: true }]
      }
      return [{ title: 'Report', url: '/dashboard/reporting/report-frameworks' }]
    },
    isCompanyAdmin() {
      this.fetchMetrics()
      const user = this.$store.auth.authenticatedUser
      const cid = JSON.parse(localStorage.getItem('cid'))
      if (user?.is_superuser || user?.is_zuno_engineer) return false
      const company = this.$store.companies.companies?.find(({ id }) => Number(id) === Number(cid))
      return company ? company?.is_member_admin : this.fetchCompanyData()
    },
  },

  mounted() {
    const { isSupplierPortalEnabled, isReportingSideAppEnabled } = useFeatureFlag()
    this.supplierPortal = isSupplierPortalEnabled()
    this.reportingExternalEnabled = isReportingSideAppEnabled()
  },

  methods: {
    async fetchCompanyData() {
      const cid = JSON.parse(localStorage.getItem('cid'))
      return await this.$apis.companies.fetchAll().then(async (response) => {
        if (response?.data) {
          this.$store.companies.setCompanies(response?.data)
          return response?.data?.find(({ id }) => Number(id) === Number(cid))?.is_member_admin
        }
        return false
      })
    },
    async fetchMetrics() {
      const cid = JSON.parse(localStorage.getItem('cid'))

      if (cid) {
        const { dataOwnerGhgAssets, approverGhgAssets } = this.$apis.esgMetrics

        await Promise.all([dataOwnerGhgAssets(cid), approverGhgAssets(cid)]).then(
          ([dataOwnerCategories, approverCategories]) => {
            this.categoriesAssignedToThisUser = dataOwnerCategories.data
            this.categoriesThisUserIsAnApproverOf = approverCategories.data
            getCurrentInstance()?.proxy?.$forceUpdate()
          },
        )
      }
    },
  },
}
</script>
